// biome-ignore-all lint/suspicious/noArrayIndexKey: action-sheet items are derived directly from request data on each render with a stable order.
// biome-ignore-all lint/security/noDangerouslySetInnerHtml: the action-sheet body renders admin-authored next-steps templates through sanitizeRichText (whitelist: b/u/span[font-size]/br), never raw user input.
// src/components/request/ActionSheetModal.jsx
// -----------------------------------------------------------------------------
// The Action Sheet modal — a draggable, printable per-session sheet for one
// child. Extracted from ShelterRequestView and rendered ONCE by RequestDetail so
// both the shelter read view and the responder view can open it (via
// setActionSheetOpen). Nothing is stored: the PDF is generated on demand.
//
// Props
//   open               — { ci, sessionDate } | null — which child+session to show
//   onClose            — () => void
//   req                — the request object
//   nextStepsTemplates — admin next-steps templates (for approved-item instructions)

import { useRef } from "react";
import { useUniformSuppliers } from "../../hooks/useUniformSuppliers";
import { getSessionItems, itemOutcomeMessage } from "../../services/actionSheets";
import { getNextStepsText } from "../../services/approvals";
import { richTextDisplayHtml } from "../../services/richText";
import { openActionSheetPdf } from "../../utils/actionSheetPdf";
import { APPROVAL_STATUSES } from "../../utils/enums";
import { GLYPH } from "../../utils/glyphs";
import { formatRef, money } from "../../utils/helpers";
import { formatPhone } from "../../utils/sanitize";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function ActionSheetModal({ open, onClose, req, nextStepsTemplates }) {
  const { suppliers: uniformSuppliers } = useUniformSuppliers();
  // The on-screen header doubles as the drag handle (moveable modal).
  const actionSheetHeaderRef = useRef(null);

  if (!open) return null;
  const child = (req.children || [])[open.ci];
  if (!child) return null;

  const items = getSessionItems(child, open.sessionDate, {
    reqState: req.state || "",
    uniformSuppliers,
  });
  const sessionLabel = new Date(open.sessionDate).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  // General info for print header
  const contactName = req.contactName || req.shelterName || "";
  const contactPhone = formatPhone(req.phone) || "";
  const deliveryAddr = req.deliveryAddress || "";
  const shelterRef = req.shelterRef || req.shelterCode || "";
  const childDesc =
    "Child " +
    (open.ci + 1) +
    " — " +
    (child.gender || "") +
    (child.yearLevel ? ` · ${child.yearLevel}` : "") +
    (child.schoolName ? ` — ${child.schoolName}` : "");

  return (
    <ModalBackdrop
      onClose={onClose}
      draggable
      dragHandleRef={actionSheetHeaderRef}
      contentStyle={{ width: 680 }}
    >
      {/* Print-only CSS: hide everything except this action sheet */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-action-sheet-print], [data-action-sheet-print] * { visibility: visible; }
          [data-action-sheet-print] { position: absolute; left: 0; top: 0; width: 100%; border: none !important; border-radius: 0 !important; padding: 12px !important; }
          .as-no-print { display: none !important; }
          .as-print-only { display: block !important; }
          .as-print-card { break-inside: avoid; }
        }
      `}</style>

      <div
        data-action-sheet-print
        style={{
          background: "#FFFFFF",
          border: `2px solid ${"#00695C"}`,
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 16,
        }}
      >
        {/* Print-only header with general info */}
        <div
          className="as-print-only"
          style={{
            display: "none",
            marginBottom: 16,
            paddingBottom: 14,
            borderBottom: `2px solid ${"#00695C"}`,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, color: "#00695C", marginBottom: 8 }}>
            Zephyr Education — Action Sheet
          </div>
          <div
            style={{ display: "flex", gap: 32, fontSize: 12, color: "#1A2E2B", lineHeight: 1.8 }}
          >
            <div>
              <div>
                <strong>Request:</strong> {formatRef(req.refNo)}
              </div>
              <div>
                <strong>DFV Service Ref:</strong> {shelterRef}
              </div>
              <div>
                <strong>Contact:</strong> {contactName}
              </div>
            </div>
            <div>
              <div>
                <strong>Phone:</strong> {contactPhone}
              </div>
              <div>
                <strong>Delivery:</strong> {deliveryAddr}
              </div>
              <div>
                <strong>Date:</strong> {sessionLabel}
              </div>
            </div>
          </div>
        </div>

        {/* Screen header — single clean line; also the drag handle. */}
        <div
          ref={actionSheetHeaderRef}
          className="as-no-print"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#00695C" }}>{childDesc}</div>
            <div style={{ fontSize: 12, color: "#607D79" }}>
              {`Session: ${sessionLabel} · Ref: ${req.refNo || "—"}`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={function () {
                const pdfItems = items.map(function (it) {
                  let message;
                  if (it.status !== APPROVAL_STATUSES.APPROVED) {
                    // db-148: Declined / More-Info shows the responder's REASON.
                    message = itemOutcomeMessage(it.status, it.reason);
                  } else {
                    const txt = getNextStepsText(nextStepsTemplates, it);
                    message = txt
                      ? txt.replace(/\{\{refNo\}\}/g, req.refNo || "—")
                      : "No instructions template found for this item type.";
                  }
                  return { status: it.status, type: it.type, amount: it.amount, message: message };
                });
                openActionSheetPdf({
                  refNo: formatRef(req.refNo),
                  shelterRef: shelterRef,
                  contact: contactName,
                  phone: contactPhone,
                  delivery: deliveryAddr,
                  date: sessionLabel,
                  childDesc: childDesc,
                  items: pdfItems,
                });
              }}
              style={{
                background: "#00695C",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {GLYPH.printer} Print
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none",
                border: `1px solid ${"#CFE0DE"}`,
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
                color: "#607D79",
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Child info line (print only — screen header already has it) */}
        <div
          className="as-print-only"
          style={{
            display: "none",
            fontSize: 13,
            fontWeight: 700,
            color: "#00695C",
            marginBottom: 10,
            paddingBottom: 8,
            borderBottom: `1px solid ${"#CFE0DE"}`,
          }}
        >
          {childDesc}
        </div>

        {items.length === 0 ? (
          <div style={{ fontSize: 13, color: "#607D79", fontStyle: "italic" }}>
            No items in this session for this child.
          </div>
        ) : (
          items.map(function (item, ii) {
            const statusBg =
              item.status === APPROVAL_STATUSES.APPROVED
                ? "#E8F5E9"
                : item.status === APPROVAL_STATUSES.DECLINED
                  ? "#FFEBEE"
                  : "#FFF8E1";
            const statusColor =
              item.status === APPROVAL_STATUSES.APPROVED
                ? "#2E7D32"
                : item.status === APPROVAL_STATUSES.DECLINED
                  ? "#C62828"
                  : "#F57F17";
            const statusBorder =
              item.status === APPROVAL_STATUSES.APPROVED
                ? "#A5D6A7"
                : item.status === APPROVAL_STATUSES.DECLINED
                  ? "#EF9A9A"
                  : "#FFE082";
            return (
              <div
                key={ii}
                className="as-print-card"
                style={{
                  background: "#F9FBF9",
                  border: `1px solid ${"#CFE0DE"}`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 4,
                      padding: "2px 8px",
                      background: statusBg,
                      color: statusColor,
                      border: `1px solid ${statusBorder}`,
                    }}
                  >
                    {item.status === APPROVAL_STATUSES.APPROVED
                      ? `${GLYPH.check} Approved`
                      : item.status === APPROVAL_STATUSES.DECLINED
                        ? `${GLYPH.ballotX} Declined`
                        : `${GLYPH.warning} ${item.status}`}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{item.type}</span>
                  {item.amount && (
                    <span style={{ fontSize: 13, color: "#388E3C", fontWeight: 600 }}>
                      {money(item.amount)}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#37474F",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {(function () {
                    // db-148: Declined / More-Info shows the responder's REASON.
                    if (item.status !== APPROVAL_STATUSES.APPROVED)
                      return itemOutcomeMessage(item.status, item.reason);
                    let txt = getNextStepsText(nextStepsTemplates, item);
                    if (!txt)
                      return (
                        <span style={{ color: "#607D79", fontStyle: "italic" }}>
                          No instructions template found for this item type.
                        </span>
                      );
                    txt = txt.replace(/\{\{refNo\}\}/g, req.refNo || "—");
                    return <span dangerouslySetInnerHTML={{ __html: richTextDisplayHtml(txt) }} />;
                  })()}
                </div>
              </div>
            );
          })
        )}
      </div>
    </ModalBackdrop>
  );
}
